import { lazy } from 'react';

import { GlobalErrorBoundary } from '@app/error-boundaries/GlobalErrorBoundary';
import { AuthGuard } from '@app/guards/AuthGuard';
import { ERPLayout } from '@app/layouts/ERPLayout';
import { ROUTES, toChildRoutePath } from '@app/navigation/registry';
import { LegacyBookingRedirect } from '@app/router/LegacyBookingRedirect';
import { Lazy } from '@app/router/RouteSuspense';
import { Navigate, type RouteObject } from 'react-router-dom';

// Lazy-load all pages for route-level code splitting
const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'));
const DashboardPage = lazy(() => import('@features/dashboard/pages/DashboardPage'));

// ─── Bookings module ───────────────────────────────────────────────────────────
const BookingsListPage = lazy(() => import('@features/bookings/pages/BookingsListPage'));
const BookingPage = lazy(() => import('@features/bookings/pages/BookingPage'));
const InvoicePage = lazy(() => import('@features/bookings/pages/InvoicePage'));

// ─── Companies module ──────────────────────────────────────────────────────────
const CompaniesPage = lazy(() => import('@features/companies/pages/CompaniesPage'));

// ─── CRM module ────────────────────────────────────────────────────────────────
const CrmCustomersPage = lazy(() => import('@features/crm/pages/CrmCustomersPage'));
const CrmCustomerProfilePage = lazy(() => import('@features/crm/pages/CrmCustomerProfilePage'));
const CrmServiceDuePage = lazy(() => import('@features/crm/pages/CrmServiceDuePage'));
const CrmRemindersPage = lazy(() => import('@features/crm/pages/CrmRemindersPage'));
const CrmOutstandingDuesPage = lazy(() => import('@features/crm/pages/CrmOutstandingDuesPage'));
const CrmCustomerPaymentPage = lazy(() => import('@features/crm/pages/CrmCustomerPaymentPage'));
const CrmAnalyticsPage = lazy(() => import('@features/crm/pages/CrmAnalyticsPage'));
const CrmVehiclesPage = lazy(() => import('@features/crm/pages/CrmVehiclesPage'));

// ─── Users module ───────────────────────────────────────────────────────────
const UsersPage = lazy(() => import('@features/users/pages/UsersPage'));

// ─── Roles module ───────────────────────────────────────────────────────────
const RolesPage = lazy(() => import('@features/roles/pages/RolesPage'));
const RoleDetailPage = lazy(() => import('@features/roles/pages/RoleDetailPage'));

// ─── Settings module ───────────────────────────────────────────────────────────
const BusinessProfilePage = lazy(() => import('@features/settings/pages/BusinessProfilePage'));
const UpiSettingsPage = lazy(() => import('@features/settings/pages/UpiSettingsPage'));
const BankSettingsPage = lazy(() => import('@features/settings/pages/BankSettingsPage'));
const PackagesSettingsPage = lazy(() => import('@features/settings/pages/PackagesSettingsPage'));
const JobCardItemsPage = lazy(() => import('@features/job-card-items/pages/JobCardItemsPage'));

// ─── Reports module ───────────────────────────────────────────────────────────
const ReportsIndexPage = lazy(() => import('@features/reports/pages/ReportsIndexPage'));
const RevenueReportPage = lazy(() => import('@features/reports/pages/RevenueReportPage'));
const OutstandingDuesReportPage = lazy(
  () => import('@features/reports/pages/OutstandingDuesReportPage'),
);
const DailyJobSheetPage = lazy(() => import('@features/reports/pages/DailyJobSheetPage'));
const CustomerStatementPage = lazy(() => import('@features/reports/pages/CustomerStatementPage'));
const TopServicesReportPage = lazy(() => import('@features/reports/pages/TopServicesReportPage'));
const GstReportPage = lazy(() => import('@features/reports/pages/GstReportPage'));

export const routes: RouteObject[] = [
  // ─── Public ────────────────────────────────────────────────────────────────
  {
    path: ROUTES.LOGIN,
    element: (
      <Lazy>
        <LoginPage />
      </Lazy>
    ),
  },

  // ─── Protected ─────────────────────────────────────────────────────────────
  {
    path: ROUTES.ROOT,
    element: (
      <GlobalErrorBoundary>
        <AuthGuard>
          <ERPLayout />
        </AuthGuard>
      </GlobalErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
      {
        path: toChildRoutePath(ROUTES.DASHBOARD),
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      // ── Bookings (unified workspace) ──────────────────────────────────────
      {
        path: toChildRoutePath(ROUTES.BOOKINGS), // 'booking'
        element: (
          <Lazy>
            <BookingsListPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.BOOKING_NEW), // 'booking/new' (static, matches first)
        element: (
          <Lazy>
            <BookingPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.BOOKING_DETAIL), // 'booking/:bookingId'
        element: (
          <Lazy>
            <BookingPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.BOOKING_INVOICE), // 'booking/:bookingId/invoice'
        element: (
          <Lazy>
            <InvoicePage />
          </Lazy>
        ),
      },
      // ── Companies ──────────────────────────────────────────────────
      {
        path: toChildRoutePath(ROUTES.COMPANIES),
        element: (
          <Lazy>
            <CompaniesPage />
          </Lazy>
        ),
      },
      // ── CRM ────────────────────────────────────────────────────────
      {
        path: toChildRoutePath(ROUTES.CRM_CUSTOMERS),
        element: (
          <Lazy>
            <CrmCustomersPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_CUSTOMER_PROFILE),
        element: (
          <Lazy>
            <CrmCustomerProfilePage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_SERVICE_DUE),
        element: (
          <Lazy>
            <CrmServiceDuePage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_REMINDERS),
        element: (
          <Lazy>
            <CrmRemindersPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_OUTSTANDING_DUES),
        element: (
          <Lazy>
            <CrmOutstandingDuesPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_OUTSTANDING_DUES_CUSTOMER),
        element: (
          <Lazy>
            <CrmCustomerPaymentPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_ANALYTICS),
        element: (
          <Lazy>
            <CrmAnalyticsPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.CRM_VEHICLES),
        element: (
          <Lazy>
            <CrmVehiclesPage />
          </Lazy>
        ),
      },
      // redirect /settings → /settings/profile
      {
        path: toChildRoutePath(ROUTES.SETTINGS),
        element: <Navigate to={ROUTES.SETTINGS_PROFILE} replace />,
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_PROFILE),
        element: (
          <Lazy>
            <BusinessProfilePage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_UPI),
        element: (
          <Lazy>
            <UpiSettingsPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_BANK),
        element: (
          <Lazy>
            <BankSettingsPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_PACKAGES),
        element: (
          <Lazy>
            <PackagesSettingsPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_JOB_CARD_ITEMS),
        element: (
          <Lazy>
            <JobCardItemsPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_ROLES),
        element: (
          <Lazy>
            <RolesPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_ROLE_NEW),
        element: (
          <Lazy>
            <RoleDetailPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.SETTINGS_ROLE_DETAIL),
        element: (
          <Lazy>
            <RoleDetailPage />
          </Lazy>
        ),
      },
      // ── Reports ────────────────────────────────────────────────────────────────────
      {
        path: toChildRoutePath(ROUTES.REPORTS),
        element: (
          <Lazy>
            <ReportsIndexPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_REVENUE),
        element: (
          <Lazy>
            <RevenueReportPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_OUTSTANDING_DUES),
        element: (
          <Lazy>
            <OutstandingDuesReportPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_DAILY_JOB_SHEET),
        element: (
          <Lazy>
            <DailyJobSheetPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_CUSTOMER_STATEMENT),
        element: (
          <Lazy>
            <CustomerStatementPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_TOP_SERVICES),
        element: (
          <Lazy>
            <TopServicesReportPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.REPORTS_GST),
        element: (
          <Lazy>
            <GstReportPage />
          </Lazy>
        ),
      },
      {
        path: toChildRoutePath(ROUTES.USERS),
        element: (
          <Lazy>
            <UsersPage />
          </Lazy>
        ),
      },
      // ── Legacy redirects: old /bookings/* → new /booking/* ──────────────────
      { path: 'bookings', element: <Navigate to={ROUTES.BOOKINGS} replace /> },
      { path: 'bookings/new', element: <Navigate to={ROUTES.BOOKING_NEW} replace /> },
      { path: 'bookings/new/workspace', element: <Navigate to={ROUTES.BOOKING_NEW} replace /> },
      { path: 'bookings/:id', element: <LegacyBookingRedirect /> },
      { path: 'bookings/:id/workspace', element: <LegacyBookingRedirect /> },
    ],
  },

  // ─── Fallback ──────────────────────────────────────────────────────────────
  {
    path: '*',
    element: <Navigate to={ROUTES.ROOT} replace />,
  },
];
